import styled from '@emotion/styled';
import { Tooltip as MuiTooltip, tooltipClasses } from '@mui/material';
import PropTypes from 'prop-types';

export const TOOLTIP_VARIANTS = {
  LIGHT: 'light',
  DARK: 'dark',
};

export const TOOLTIP_PLACEMENTS = [
  'bottom-end',
  'bottom-start',
  'bottom',
  'left-end',
  'left-start',
  'left',
  'right-end',
  'right-start',
  'right',
  'top-end',
  'top-start',
  'top',
];

const StyledTooltip = styled(({ className, ...props }) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ styles = {}, theme, variant }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: (variant === TOOLTIP_VARIANTS.LIGHT) ? theme.palette.common.white : theme.palette.grey[100],
    color: (variant === TOOLTIP_VARIANTS.LIGHT) ? theme.palette.grey[100] : theme.palette.common.white,
    padding: '6px',
    boxShadow: '0 4px 8px 0 rgb(0 0 0 / 20%), 0 6px 20px 0 rgb(0 0 0 / 20%)',
    textAlign: 'center',
    borderRadius: '4px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: (variant === TOOLTIP_VARIANTS.LIGHT) ? theme.palette.grey[40] : theme.palette.grey[100],
    /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.small.fontSize,
    fontWeight: theme.typography.small.fontWeight,
    lineHeight: theme.typography.small.lineHeight,
    ...styles,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: (variant === TOOLTIP_VARIANTS.LIGHT) ? theme.palette.common.white : theme.palette.grey[100],
    '&:before': {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: (variant === TOOLTIP_VARIANTS.LIGHT) ? theme.palette.grey[40] : theme.palette.grey[100],
    },
  },
}));

/**
* Renders a <Tooltip /> component. Wrapper for MUI Tooltip, so it shares all its properties. arrow and placement, specially
* @param {Object} props
* @param {node | node[]} props.children - Components which will be wrapped by the tooltip
* @param {node} props.title - Text for the tooltip
* @param {string} props.variant - light or dark
*/
export const Tooltip = ({
  title,
  children = null,
  variant = TOOLTIP_VARIANTS.LIGHT,
  placement = 'bottom',
  ...rest
}) => (
  <StyledTooltip
    title={title}
    variant={variant}
    placement={placement}
    {...rest}
  >
    {children}
  </StyledTooltip>
);

Tooltip.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  title: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    TOOLTIP_VARIANTS.LIGHT,
    TOOLTIP_VARIANTS.DARK,
  ]),
  placement: PropTypes.oneOf(TOOLTIP_PLACEMENTS),
};
